/**
 * The measurement sets visitors pick in the Measure panel (the ruler button's menu): a tick box
 * per measurement set that holds saved measurements, then "Other measurements" for the ones in no
 * set, and a "Show all measurements" row when there are two or more. Every row starts ticked, or
 * every row unticked when the creator chose that (`measurementSetsStartHidden`): then nothing saved
 * shows until a visitor ticks a set. Unticking hides that set's saved measurements; visitors' own
 * measurements are not affected (utils/measurementSets.ts). Ticked sets stay drawn after the panel
 * closes.
 *
 * Offered only when the creator lets visitors pick sets (the scene's `measurementSetsInLayersMenu`,
 * named for where viewers 2.12.1 and 2.12.2 put them: a section of the Layers menu) and a set holds
 * saved measurements.
 *
 * Built with DOM APIs only (no innerHTML of authored text), so set names can't inject markup.
 */
import { OTHER_MEASUREMENTS_LABEL, OTHER_MEASUREMENTS_SET_ID, type MeasurementSetsState } from '../utils/measurementSets';

/** Default English heading of the Measure panel (buttonLabels.measureTitle). */
export const MEASUREMENTS_SECTION_TITLE = 'Measurements';
/** Default English label of the row that ticks every set (buttonLabels.measureAllSets). */
export const SHOW_ALL_MEASUREMENTS_LABEL = 'Show all measurements';
/** Default English heading of the sets (buttonLabels.measureSets). */
export const MEASUREMENT_SETS_HEADING = 'Measurement sets';

/** Whether visitors pick the scene's measurement sets (in the Measure panel). */
export function offersMeasurementSetPicker(scene: { measurementSetsInLayersMenu?: unknown }, sets: MeasurementSetsState | null): sets is MeasurementSetsState {
    return scene.measurementSetsInLayersMenu === true && !!sets && sets.active;
}

/**
 * Whether the scene's sets start unticked: the creator chose it (`measurementSetsStartHidden`) and
 * visitors pick the sets. Without the picker nothing would show them again, so the setting is
 * ignored.
 */
export function measurementSetsStartHidden(
    scene: { measurementSetsInLayersMenu?: unknown; measurementSetsStartHidden?: unknown },
    sets: MeasurementSetsState | null,
): sets is MeasurementSetsState {
    return scene.measurementSetsStartHidden === true && offersMeasurementSetPicker(scene, sets);
}

/** The picker's words (the scene's label overrides, else English). */
export interface MeasurementSetPickerLabels {
    heading: string;
    /** "Other measurements": the creator's measurements in no set. */
    other: string;
    /** The row that ticks every set, or unticks them all when they all are. */
    all: string;
}

export const DEFAULT_MEASUREMENT_SET_PICKER_LABELS: MeasurementSetPickerLabels = {
    heading: MEASUREMENT_SETS_HEADING,
    other: OTHER_MEASUREMENTS_LABEL,
    all: SHOW_ALL_MEASUREMENTS_LABEL,
};

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] => {
    const node = document.createElement(tag);
    node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
};

/** One tick box row: `checked` is true, false or 'mixed' (the "all" row with some sets ticked). */
function pickerRow(name: string, checked: boolean | 'mixed', id: string, count?: number): HTMLButtonElement {
    const row = el('button', 'storysplat-measure-set');
    row.type = 'button';
    row.setAttribute('role', 'checkbox');
    row.setAttribute('aria-checked', checked === 'mixed' ? 'mixed' : checked ? 'true' : 'false');
    row.dataset.measureSetId = id;
    const box = el('span', 'storysplat-measure-set-box');
    box.setAttribute('aria-hidden', 'true');
    row.append(box, el('span', 'storysplat-measure-set-name', name));
    if (count !== undefined) {
        const badge = el('span', 'storysplat-measure-set-count', String(count));
        badge.setAttribute('aria-hidden', 'true');
        row.appendChild(badge);
        row.setAttribute('aria-label', `${name} (${count})`);
    }
    row.title = name;
    return row;
}

/** Numbers the headings' ids (each viewer's panel re-renders its picker under a new one). */
let pickerCount = 0;

/** Id of the "all" row (data-measure-set-id; never a set id, which can't be empty). */
export const ALL_MEASUREMENT_SETS_ROW = '';

/**
 * The sets block of the Measure panel, or null when there is nothing to pick. Clicking a row
 * switches the set in `sets`; the panel re-renders from the state's onChange, so this draws the
 * state as it is now and holds no state of its own.
 */
export function buildMeasurementSetPicker(sets: MeasurementSetsState, labels: MeasurementSetPickerLabels = DEFAULT_MEASUREMENT_SET_PICKER_LABELS): HTMLElement | null {
    const entries = sets.menuEntries();
    if (entries.length === 0) return null;
    const block = el('div', 'storysplat-measure-sets');
    block.setAttribute('role', 'group');
    const heading = el('div', 'storysplat-measure-sets-title', labels.heading);
    heading.id = `storysplat-measure-sets-${++pickerCount}`;
    block.setAttribute('aria-labelledby', heading.id);
    block.appendChild(heading);
    const stop = (fn: () => void) => (e: Event) => {
        e.stopPropagation();
        fn();
    };
    if (entries.length > 1) {
        const all = sets.allVisible() ? true : sets.noneVisible() ? false : 'mixed';
        const allRow = pickerRow(labels.all, all, ALL_MEASUREMENT_SETS_ROW);
        allRow.classList.add('storysplat-measure-set--all');
        allRow.addEventListener('click', stop(() => sets.setAllVisible(!sets.allVisible())));
        block.appendChild(allRow);
    }
    for (const entry of entries) {
        const name = entry.id === OTHER_MEASUREMENTS_SET_ID ? labels.other : entry.name;
        const row = pickerRow(name, entry.visible, entry.id, entry.count);
        row.addEventListener('click', stop(() => sets.setVisible(entry.id, !sets.isShown(entry.id))));
        block.appendChild(row);
    }
    return block;
}
